import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { Roles } from "~enums/roles";
import { useAddRole } from "~hooks/apiHooks/useAddRole";
import { useApi } from "~hooks/apiHooks/useApi";
import { useNotification } from "~hooks/useNotification";
import type { UpsertFixtureRosterModel } from "~pages/Roster/types/upsertFixtureRosterModel";
import type { UpsertSeasonRosterModel } from "~pages/Roster/types/upsertSeasonRosterModel";
import type { Roster, RosterResponse } from "~types/roster";

const useUpsertRosterPlayer = () => {
  const { sport, organizationId, seasonId, fixtureId } = useParams();
  const request = useApi();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();

  const { mutateAsync: createCaptainRole } = useAddRole();
  const notification = useNotification();
  return useMutation<RosterResponse, Error, UpsertSeasonRosterModel | UpsertFixtureRosterModel>({
    mutationFn: async (upsertModel: UpsertSeasonRosterModel | UpsertFixtureRosterModel) => {
      const url = fixtureId
        ? `${sport}/o/${organizationId}/fixtures/${fixtureId}/roster`
        : `${sport}/o/${organizationId}/seasons/${seasonId}/roster`;
      const method = "POST";
      const body = { ...upsertModel, isCaptain: undefined };
      return await request(url, { method, body });
    },
    onSuccess: async (response: { data: Array<Roster> }, upsertModel) => {
      if (response?.data && response.data.length > 0) {
        if (upsertModel.isCaptain && seasonId) {
          await createCaptainRole({
            personId: upsertModel.personId,
            entityId: upsertModel.entityId,
            seasonId,
            role: Roles.Captain,
            fixtureId: fixtureId,
          });
        }
        const queryKeyId = fixtureId ? "fixtureRoster" : "seasonRoster";
        await queryClient.invalidateQueries({
          queryKey: [queryKeyId, sport, organizationId, seasonId],
        });
        notification.success({
          message: formatMessage({ id: "roster.update.success" }),
          placement: "bottomRight",
        });
      }
    },
  });
};

export default useUpsertRosterPlayer;
